import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Save } from "lucide-react";

import BreadCrumb from "@/Components/Common/BreadCrumb";
import { useAuthContext } from "@/Components/AuthContext";
import { Badge, Button, Card, CardBody, CardHeader, Col, Input, Label, Row } from "@/Components/ui";
import { groupReservationService } from "@/api/services/group-reservation.service";
import {
  GroupReservationGroupType,
  GroupReservationOptionProduct,
  GroupReservationRequestCreatePayload,
} from "@/api/types/group-reservation";
import GroupReservationPageShell from "../GroupReservationPageShell";

type CreateForm = {
  group_type: GroupReservationGroupType;
  group_name: string;
  applicant_name: string;
  applicant_phone: string;
  applicant_email: string;
  visit_start_date: string;
  preferred_time: string;
  headcount: string;
  adult_count: string;
  child_count: string;
  leader_count: string;
  request_memo: string;
  option_product_ids: number[];
  option_product_quantities: Record<number, string>;
};

const emptyForm = (): CreateForm => ({
  group_type: "general",
  group_name: "",
  applicant_name: "",
  applicant_phone: "",
  applicant_email: "",
  visit_start_date: "",
  preferred_time: "",
  headcount: "",
  adult_count: "",
  child_count: "",
  leader_count: "",
  request_memo: "",
  option_product_ids: [],
  option_product_quantities: {},
});

const parseOptionalNumber = (value: string) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseRequiredNumber = (value: string) => parseOptionalNumber(value) ?? 0;

const formatAmount = (value?: number | null) => (value ? `${Number(value).toLocaleString()}원` : "0원");

const optionQuantity = (form: CreateForm, optionId: number) => {
  const parsed = parseRequiredNumber(form.option_product_quantities[optionId] || "");
  if (parsed > 0) return parsed;
  return Math.max(parseRequiredNumber(form.headcount), 1);
};

const buildPayload = (form: CreateForm): GroupReservationRequestCreatePayload => {
  const isAcademy = form.group_type === "academy";
  return {
    group_type: form.group_type,
    group_name: form.group_name.trim(),
    applicant_name: form.applicant_name.trim(),
    applicant_phone: form.applicant_phone.trim(),
    applicant_email: form.applicant_email.trim() || null,
    visit_start_date: form.visit_start_date,
    visit_end_date: form.visit_start_date,
    preferred_time: form.preferred_time.trim() || null,
    headcount: parseRequiredNumber(form.headcount),
    adult_count: isAcademy ? parseOptionalNumber(form.adult_count) : null,
    child_count: isAcademy ? parseOptionalNumber(form.child_count) : null,
    leader_count: isAcademy ? parseOptionalNumber(form.leader_count) : null,
    preferred_package_id: null,
    option_product_ids: form.option_product_ids,
    option_products: form.option_product_ids.map((optionId) => ({
      option_product_id: optionId,
      quantity: optionQuantity(form, optionId),
    })),
    request_memo: form.request_memo.trim() || null,
  };
};

const validateForm = (form: CreateForm) => {
  const errors: Record<string, string> = {};
  const headcount = parseRequiredNumber(form.headcount);
  if (!form.visit_start_date) errors.visit_start_date = "희망 방문일을 선택해주세요.";
  if (!form.group_name.trim()) errors.group_name = "단체명 또는 기관명을 입력해주세요.";
  if (headcount < 2) errors.headcount = "총 인원은 2명 이상이어야 합니다.";
  if (form.group_type === "academy") {
    const detailTotal =
      parseRequiredNumber(form.adult_count) +
      parseRequiredNumber(form.child_count) +
      parseRequiredNumber(form.leader_count);
    if (detailTotal > headcount) errors.headcount = "세부 인원 합계는 총 인원을 초과할 수 없습니다.";
  }
  if (!form.applicant_name.trim()) errors.applicant_name = "신청자명을 입력해주세요.";
  if (!form.applicant_phone.trim()) errors.applicant_phone = "신청자 연락처를 입력해주세요.";
  return errors;
};

const FormField = ({
  id,
  label,
  error,
  children,
}: {
  id?: string;
  label: string;
  error?: string;
  children: React.ReactNode;
}) => (
  <div className="mb-3">
    <Label htmlFor={id}>{label}</Label>
    {children}
    {error ? <div className="mt-1 text-danger fs-12 fw-semibold">{error}</div> : null}
  </div>
);

export default function GroupReservationRequestCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userInfo } = useAuthContext();
  const isMarketerOnly = Boolean(
    userInfo?.capabilities?.includes("marketer") &&
      !userInfo?.is_global_admin &&
      !userInfo?.capabilities?.includes("site_manager") &&
      !userInfo?.capabilities?.includes("site_owner")
  );
  const [form, setForm] = useState<CreateForm>(emptyForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isAcademy = form.group_type === "academy";

  const optionQuery = useQuery({
    queryKey: ["group-reservation", "admin-option-products", "create"],
    queryFn: () => groupReservationService.getOptionProducts(),
    enabled: !isMarketerOnly,
  });

  const optionProducts = useMemo(
    () => (optionQuery.data?.data.results ?? []).filter((option) => option.is_active),
    [optionQuery.data?.data.results]
  );

  const createMutation = useMutation({
    mutationFn: (payload: GroupReservationRequestCreatePayload) =>
      isMarketerOnly ? groupReservationService.createMarketerRequest(payload) : groupReservationService.createRequest(payload),
    onSuccess: (response) => {
      toast.success("단체예약 신청을 등록했습니다.");
      queryClient.invalidateQueries({ queryKey: ["group-reservation"] });
      navigate(isMarketerOnly ? "/marketer-info" : `/group-reservation-requests/${response.data.id}`);
    },
    onError: (error: any) => toast.error(error?.message || "단체예약 신청 등록에 실패했습니다."),
  });

  const updateField = (field: keyof CreateForm, value: string | number[] | Record<number, string>) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const toggleOption = (optionId: number, checked: boolean) => {
    const selectedIds = new Set(form.option_product_ids);
    const quantities = { ...form.option_product_quantities };
    if (checked) {
      selectedIds.add(optionId);
      if (!quantities[optionId]) quantities[optionId] = String(Math.max(parseRequiredNumber(form.headcount), 1));
    } else {
      selectedIds.delete(optionId);
      delete quantities[optionId];
    }
    updateField("option_product_ids", Array.from(selectedIds));
    updateField("option_product_quantities", quantities);
  };

  const optionTotal = (option: GroupReservationOptionProduct) => {
    const quantity = optionQuantity(form, option.id);
    return quantity * Number(option.weekday_price || 0);
  };

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validateForm(form);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      toast.error("필수 입력값을 확인해주세요.");
      return;
    }
    createMutation.mutate(buildPayload(form));
  };

  return (
    <GroupReservationPageShell>
      <BreadCrumb title="단체예약 신청 등록" pageTitle="단체예약" />
      <form onSubmit={submit}>
        <Row className="g-3">
          <Col xl={7}>
            <Card>
              <CardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <span>신청 정보</span>
                {isMarketerOnly ? <Badge color="info">마케터 대행 등록</Badge> : <Badge color="secondary">관리자 대행 등록</Badge>}
              </CardHeader>
              <CardBody>
                <Row className="g-3">
                  <Col md={4}>
                    <FormField id="group-type" label="단체 타입">
                      <Input
                        id="group-type"
                        type="select"
                        value={form.group_type}
                        onChange={(event) => updateField("group_type", event.target.value as GroupReservationGroupType)}
                      >
                        <option value="general">일반</option>
                        <option value="academy">학원</option>
                      </Input>
                    </FormField>
                  </Col>
                  <Col md={8}>
                    <FormField id="group-name" label="단체명/기관명" error={errors.group_name}>
                      <Input id="group-name" value={form.group_name} onChange={(event) => updateField("group_name", event.target.value)} />
                    </FormField>
                  </Col>
                  <Col md={6}>
                    <FormField id="visit-date" label="희망 방문일" error={errors.visit_start_date}>
                      <Input id="visit-date" type="date" value={form.visit_start_date} onChange={(event) => updateField("visit_start_date", event.target.value)} />
                    </FormField>
                  </Col>
                  <Col md={6}>
                    <FormField id="preferred-time" label="희망 시간대">
                      <Input id="preferred-time" value={form.preferred_time} onChange={(event) => updateField("preferred_time", event.target.value)} placeholder="예: 10:00" />
                    </FormField>
                  </Col>
                </Row>
                <Row className="g-3">
                  <Col md={isAcademy ? 3 : 12}>
                    <FormField id="headcount" label={isAcademy ? "총 인원" : "인원 수"} error={errors.headcount}>
                      <Input id="headcount" type="number" min="2" value={form.headcount} onChange={(event) => updateField("headcount", event.target.value)} />
                    </FormField>
                  </Col>
                  {isAcademy ? (
                    <>
                      <Col md={3}>
                        <FormField id="leader-count" label="인솔자 수">
                          <Input id="leader-count" type="number" min="0" value={form.leader_count} onChange={(event) => updateField("leader_count", event.target.value)} />
                        </FormField>
                      </Col>
                      <Col md={3}>
                        <FormField id="adult-count" label="성인 수">
                          <Input id="adult-count" type="number" min="0" value={form.adult_count} onChange={(event) => updateField("adult_count", event.target.value)} />
                        </FormField>
                      </Col>
                      <Col md={3}>
                        <FormField id="child-count" label="아동 수">
                          <Input id="child-count" type="number" min="0" value={form.child_count} onChange={(event) => updateField("child_count", event.target.value)} />
                        </FormField>
                      </Col>
                    </>
                  ) : null}
                </Row>
                <Row className="g-3">
                  <Col md={4}>
                    <FormField id="applicant-name" label="신청자명" error={errors.applicant_name}>
                      <Input id="applicant-name" value={form.applicant_name} onChange={(event) => updateField("applicant_name", event.target.value)} />
                    </FormField>
                  </Col>
                  <Col md={4}>
                    <FormField id="applicant-phone" label="연락처" error={errors.applicant_phone}>
                      <Input id="applicant-phone" value={form.applicant_phone} onChange={(event) => updateField("applicant_phone", event.target.value)} />
                    </FormField>
                  </Col>
                  <Col md={4}>
                    <FormField id="applicant-email" label="이메일">
                      <Input id="applicant-email" type="email" value={form.applicant_email} onChange={(event) => updateField("applicant_email", event.target.value)} />
                    </FormField>
                  </Col>
                </Row>
                <FormField id="request-memo" label="요청사항">
                  <Input id="request-memo" type="textarea" rows={4} value={form.request_memo} onChange={(event) => updateField("request_memo", event.target.value)} />
                </FormField>
              </CardBody>
            </Card>
          </Col>
          <Col xl={5}>
            <Card>
              <CardHeader>단체예약 옵션</CardHeader>
              <CardBody>
                {optionQuery.isLoading ? (
                  <div className="py-4 text-center text-muted">옵션을 불러오는 중입니다.</div>
                ) : optionProducts.length === 0 ? (
                  <div className="py-4 text-center text-muted">사용 가능한 단체예약 옵션이 없습니다.</div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {optionProducts.map((option) => {
                      const selected = form.option_product_ids.includes(option.id);
                      return (
                        <div key={option.id} className="rounded border p-3">
                          <label className="d-flex align-items-start gap-2">
                            <Input type="checkbox" checked={selected} onChange={(event) => toggleOption(option.id, event.target.checked)} className="mt-1" />
                            <span className="flex-grow-1">
                              <span className="d-block fw-semibold">{option.name}</span>
                              <span className="d-block text-muted fs-12">주중 {formatAmount(option.weekday_price)} / 주말 {formatAmount(option.weekend_price)}</span>
                            </span>
                          </label>
                          {selected ? (
                            <div className="mt-2 d-flex align-items-center gap-2">
                              <Label className="mb-0 fs-12">수량</Label>
                              <Input
                                type="number"
                                min="1"
                                value={form.option_product_quantities[option.id] || ""}
                                onChange={(event) =>
                                  updateField("option_product_quantities", {
                                    ...form.option_product_quantities,
                                    [option.id]: event.target.value,
                                  })
                                }
                                className="max-w-[7rem]"
                              />
                              <span className="text-muted fs-12">예상 {formatAmount(optionTotal(option))}</span>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardBody>
            </Card>
            <div className="d-flex flex-column flex-sm-row justify-content-end gap-2">
              <Button color="soft-secondary" onClick={() => navigate(isMarketerOnly ? "/marketer-info" : "/group-reservation-requests")}>
                취소
              </Button>
              <Button type="submit" color="dark" disabled={createMutation.isPending}>
                <Save size={16} className="me-1" />
                {createMutation.isPending ? "등록 중" : "신청 등록"}
              </Button>
            </div>
          </Col>
        </Row>
      </form>
    </GroupReservationPageShell>
  );
}
